import { useQuery } from "@tanstack/react-query";
import { screeningQuestionsCheckout } from "@fm/api/screening-questions-checkout";
import type { ApiClient } from "@fm/api/types";
import type {
  CheckoutScreeningQuestion,
  ScreeningQuestionFlow,
} from "@fm/api/screening-questions-checkout";
import { getApiBaseUrl } from "../lib/config";

export interface UseScreeningQuestionsCheckoutOptions {
  apiKey: string;
  fleetId: string;
  /**
   * `undefined` while the vehicle's effective booking mode is not yet
   * known — the query stays disabled rather than guessing a flow and
   * re-fetching once the real one resolves.
   */
  flow: ScreeningQuestionFlow | undefined;
  apiBaseUrl?: string;
  enabled?: boolean;
}

/**
 * Hook to fetch the active booking-questions catalog a checkout should
 * render for one flow (`instant` | `request`). Mirrors `useInsuranceOptions`'s
 * shape: a bearer-API-key `ApiClient`, a 5-minute `staleTime`, and gating on
 * the value(s) the query actually depends on — here `fleetId` and `flow`.
 */
export function useScreeningQuestionsCheckout(
  options: UseScreeningQuestionsCheckoutOptions,
) {
  const { apiKey, fleetId, flow, apiBaseUrl, enabled = true } = options;

  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };

  return useQuery<CheckoutScreeningQuestion[], Error>({
    queryKey: ["screening-questions-checkout", fleetId, flow],
    queryFn: async () => {
      const response = await screeningQuestionsCheckout.get(
        apiClient,
        flow as ScreeningQuestionFlow,
      );
      return response.data;
    },
    enabled: enabled && !!fleetId && !!flow,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
